import { NavLink, useNavigate } from "react-router-dom"
import styles from "./sidebar.module.scss"
import { useCallback, useContext, useEffect, useState } from "react"
import { SidebarContext } from "./SidebarContext"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { NotificationContext } from "components/Notification/NotificationContext"
import { faBell, faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { StudentNotification } from "shared/types/notificationTypes"
import { toast } from "react-toastify"
import { AuthContext } from "AuthContext"
import { updateStudentNotificationStatus } from "shared/routes/notifications/studentNotifications"
import studentRoute from "shared/routes/student"
import { SubmissionStudentState } from "pages/Dashboard/Student/Grades/Submission/SubmissionStudent"
import NotificationToastMessage from "components/Dashboards/Student/Notifications/NotificationToastMessage"

export interface SidebarLink {
    title: string
    path: string
    icon?: IconProp
}

interface Props {
    links: Array<SidebarLink | "separator">
}

export default function SidebarLinks({ links }: Props) {
    const { collapsed, toggleCollapse } = useContext(SidebarContext) // collapse sidebar when a sidebar link is clicked on Only If it's mobile screen
    const { isStudent } = useContext(AuthContext)
    const { notificationEvent } = useContext(NotificationContext)
    const [notificationReceived, setNotificationReceived] = useState<boolean>(false)

    const navigate = useNavigate()

    function onClickNavLink(link: SidebarLink) {
        if (!collapsed && window.innerWidth <= 800) {
            toggleCollapse()
        }
        if (link.title === "Notifications") {
            setNotificationReceived(false)
        }
    }

    const openNotification = useCallback(async (notification: StudentNotification) => {
        const validNotificationTypes = ["submission-graded", "submission-rework", "submission-reject"] //there might be more types in the future
        if (!validNotificationTypes.includes(notification.type)) {
          return
        }
        await updateStudentNotificationStatus(notification.id, "read") // mark as read after click open notification
        setNotificationReceived(false)
    
        const assignment = await studentRoute.getAssignment(notification.assignment.id)
        if (notification.assignment && !assignment) {
          toast.error("There was an error opening the notification.")
          return
        }
    
        const submissionIndex = assignment.submissions.findIndex((submission) => submission.id === notification.submissionId)
        navigate("/dashboard/student/grades/submission", {
          state: {
              currentIndex: submissionIndex,
              assignment: assignment,
              submission: assignment.submissions[submissionIndex],
              studentId: notification.studentId,
              userType: "Student",
              previousPage: "/dashboard/student/notifications",
          } as SubmissionStudentState
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isStudent || !notificationEvent) return
        if (notificationEvent.type === "unread-notifications" || notificationEvent.type === "new-notification") {
            setNotificationReceived(true)
            if (notificationEvent.type === "new-notification" && notificationEvent.notification) {
                toast.info(
                    <NotificationToastMessage notificationEvent={notificationEvent} onOpenNotification={openNotification} closeToast={this} />, 
                    { icon: <FontAwesomeIcon icon={faBell} />, autoClose: 8000, closeOnClick: false, }
                )
            }
        }
    }, [isStudent, notificationEvent, openNotification])

    return (
        <ul className={styles.sidebarLinks}>
            {links.map((link, index) => {
                if (link === "separator") {
                    return <hr key={`separator-${index}`} />
                } else {
                    const isNotificationLink = link.title === "Notifications"
                    return <NavLink to={link.path} key={link.title + link.path} onClick={() => onClickNavLink(link)}>
                        {
                            ({ isActive }) =>
                                <li className={`${isActive ? styles.active : ""} ${collapsed ? styles.collapsedLink : ""} ${isNotificationLink && !notificationReceived && "text-secondary"}`}>
                                    {link.icon && <FontAwesomeIcon icon={link.icon} className={collapsed ? "" : "me-2"} />}
                                    {collapsed ? "" : link.title}
                                    {isNotificationLink && notificationReceived && <FontAwesomeIcon icon={faCircleExclamation} className="text-primary ms-1 position-absolute" size="lg" />}
                                </li>
                        }
                    </NavLink>
                }
            })}
        </ul>
    )
}
